import { Box, Stack } from '@mui/material';
import { useMemo } from 'react';

import { CheckSquareIcon } from '@/components/icons/CheckSquareIcon';
import { IconProps } from '@/components/icons/ChevronDownIcon';
import { Home03Icon } from '@/components/icons/Home03Icon';
import { Rocket01Icon } from '@/components/icons/Rocket01Icon';
import { SearchSmIcon } from '@/components/icons/SearchSmIcon';
import { Settings01Icon } from '@/components/icons/Settings01Icon';
import { Users01Icon } from '@/components/icons/Users01Icon';
import { Tooltip } from '@/components/poppers/Tooltip/Tooltip';
import { useModalState } from '@/hooks/useModalState';
import { useCurrentUser } from '@/modules/authentication/hooks/useCurrentUser';
import { GlobalSearch } from '@/modules/globalSearch/GlobalSearch';
import { usePresentationApprovalBadgeCount } from '@/modules/presentationApprovals/PresentationApprovalBadge/usePresentationApprovalBadgeCount';
import { ROUTE_KEYS } from '@/navigation/constants';
import { useCanAccessPage } from '@/navigation/usePageAccessLookup';
// this is an appropriate usage of a page-specific import
// eslint-disable-next-line luminary-rules/no-page-imports
import { useAuthorizedSettingsPages } from '@/pages/admin/AdminUsersPage/hooks/useAuthorizedAdminPages';

import { SidebarAvatar } from '../../Sidebar/components/SidebarAvatar';
import { UserSettingsTooltip } from '../../Sidebar/components/UserSettingsTooltip';
import { MenuItem } from '../../Sidebar/MenuItem';

const iconProps: IconProps = {
  size: 16,
  sx: {
    display: 'flex',
  },
};

interface AppBarMenuItemsProps {
  appBarTheme?: 'dark' | 'light';
  disableActiveHighlight?: boolean;
}

export function AppBarMenuItems({
  appBarTheme = 'dark',
  disableActiveHighlight = false,
}: AppBarMenuItemsProps) {
  const currentUser = useCurrentUser();
  const canAccessTasksPage = useCanAccessPage(ROUTE_KEYS.TASKS);
  const newApprovalRequestCount = usePresentationApprovalBadgeCount();
  const canAccessCollaboratorsPage = useCanAccessPage(ROUTE_KEYS.COLLABORATORS);

  // if someone has access to >0 admin pages, we want to show the admin menu item,
  // and route them to the first tab that they have access to
  const eligibleSettingsPages = useAuthorizedSettingsPages();
  const firstEligibleSettingsPage = eligibleSettingsPages[0];

  const [{ isModalOpen: isOpen }, modalFunctions] = useModalState();

  const commonMenuItemProps = useMemo(
    () => ({
      variant: 'small' as const,
      colorTheme: appBarTheme,
      highlightWhenActive: true,
      isActive: disableActiveHighlight ? false : undefined,
    }),
    [appBarTheme, disableActiveHighlight]
  );

  return (
    <>
      <Stack
        component="ul"
        alignItems="center"
        spacing={0.5}
        direction="row"
        // Strip the browser's default ul padding (40px inline-start) and
        // margin; there is no global CSS reset to do it for us.
        sx={{ m: 0, p: 0 }}
      >
        <Tooltip placement="bottom" title="Clients">
          <MenuItem
            text="Clients"
            to="/households"
            icon={<Home03Icon {...iconProps} />}
            {...commonMenuItemProps}
          />
        </Tooltip>
        {canAccessTasksPage && (
          <Tooltip placement="bottom" title="Tasks">
            <MenuItem
              text="Tasks"
              to="/tasks"
              icon={<CheckSquareIcon {...iconProps} />}
              badgeCount={newApprovalRequestCount}
              badgeLabel={`${newApprovalRequestCount} new presentation approval ${newApprovalRequestCount === 1 ? 'request' : 'requests'}`}
              {...commonMenuItemProps}
            />
          </Tooltip>
        )}
        {canAccessCollaboratorsPage && (
          <Tooltip placement="bottom" title="Collaborators">
            <MenuItem
              text="Collaborators"
              to="/collaborators"
              icon={<Users01Icon {...iconProps} />}
              {...commonMenuItemProps}
            />
          </Tooltip>
        )}
        {firstEligibleSettingsPage && (
          <Tooltip placement="bottom" title="Settings">
            <MenuItem
              text="Settings"
              showWarningBadge={currentUser?.apiUserIntegrationLoginExpired}
              to={firstEligibleSettingsPage.to}
              icon={<Settings01Icon {...iconProps} />}
              {...commonMenuItemProps}
            />
          </Tooltip>
        )}
        {currentUser?.isInternalUser && (
          <Tooltip placement="bottom" title="Dev tools">
            <MenuItem
              text="Dev tools"
              to="/internal/dev-tools"
              icon={<Rocket01Icon {...iconProps} />}
              {...commonMenuItemProps}
            />
          </Tooltip>
        )}
        <Tooltip placement="bottom" title="Search">
          <MenuItem
            text="Search"
            icon={<SearchSmIcon aria-label="Search" {...iconProps} />}
            to="/search"
            onClick={() => {
              modalFunctions.openModal();
            }}
            {...commonMenuItemProps}
            highlightWhenActive={false}
          />
        </Tooltip>
        <Box pl={0.5}>
          <Tooltip
            leaveDelay={250}
            placement="bottom"
            title={<UserSettingsTooltip />}
            PopperProps={{
              disablePortal: true, // This is a fix for a scrolling issue
            }}
          >
            <Box
              sx={{
                cursor: 'default',
                position: 'relative',
              }}
            >
              <SidebarAvatar size="xs" isExpanded={false} />
            </Box>
          </Tooltip>
        </Box>
      </Stack>
      <GlobalSearch
        isOpen={isOpen}
        onClose={() => {
          modalFunctions.closeModal();
        }}
        openModal={modalFunctions.openModal}
      />
    </>
  );
}
